// web-commands.ts — the PER-APP half of the web shape: maps oriora_scout's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. oriora_scout has NO signals.rs "paid draw" — every command is a
// plain local data op (research history lives entirely on-device), so nothing is shelled here: even
// on-device history search runs in JS. The one billed thing (the research loop) is in brain.ts and
// is unchanged; these commands only persist/read what the loop produces.

import type { CommandHandler, Row, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const BRIEFS = "briefs";
const SOURCES = "sources";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

/** Clean host label from a URL — mirrors db.rs::domain_of (pure string work, no network). */
function domainOf(url: string): string {
  const s = String(url ?? "").trim();
  const afterScheme = s.includes("://") ? s.slice(s.indexOf("://") + 3) : s;
  let host = afterScheme.split(/[/?#]/)[0] ?? afterScheme;
  host = host.split("@").pop() ?? host; // strip userinfo
  host = host.split(":")[0] ?? host; // strip port
  return host.trim().toLowerCase();
}

/** History list row (no body_json) — mirrors db.rs::map_meta / BriefMeta. */
function toMeta(r: Row): Row {
  return {
    id: String(r.id),
    question: String(r.question ?? ""),
    status: String(r.status ?? "running"),
    summary: String(r.summary ?? ""),
    source_count: Number(r.source_count ?? 0),
    elapsed_ms: Number(r.elapsed_ms ?? 0),
    created_at: String(r.created_at ?? ""),
    updated_at: String(r.updated_at ?? ""),
  };
}

/** Newest-first by created_at (mirrors ORDER BY created_at DESC). */
function newestFirst(rows: Row[]): Row[] {
  return rows.sort((a, b) =>
    String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")),
  );
}

const STOPWORDS = new Set([
  "where", "what", "which", "when", "who", "whom", "whose", "how", "why",
  "the", "and", "for", "from", "with", "that", "this", "these", "those",
  "did", "does", "was", "were", "are", "have", "has", "had", "get", "got",
  "about", "any", "all", "some", "can", "could", "would", "should", "will",
  "research", "find", "tell", "show", "give",
]);

/** FTS-ish tokens — mirrors db.rs::fts_tokens (split, lowercase, len>2, drop stopwords). */
function ftsTokens(raw: string): string[] {
  return String(raw ?? "")
    .split(/[^0-9a-zA-Z_]+/)
    .filter((t) => t.length > 0)
    .map((t) => t.toLowerCase())
    .filter((t) => t.length > 2 && !STOPWORDS.has(t));
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {
    // ── run lifecycle ──
    // Open a new research run (status `running`) and return its id.
    begin_brief: ({ question }) => {
      const q = String(question ?? "").trim() || "Untitled research";
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        question: q,
        status: "running",
        summary: "",
        body_json: "{}",
        source_count: 0,
        elapsed_ms: 0,
        created_at: now,
        updated_at: now,
      };
      db.insert(BRIEFS, row);
      return row.id;
    },
    // Finish a run: store summary + full brief JSON + sources, flip to `done`. Returns the full Brief.
    complete_brief: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const id = String(i.id ?? "");
      const existing = db.get(BRIEFS, id);
      if (!existing) return null; // mirrors "changed == 0 → None"
      const srcIn = Array.isArray(i.sources) ? (i.sources as Record<string, unknown>[]) : [];
      // Replace any prior sources for this brief (a retry re-completes the same row).
      for (const s of db.all(SOURCES)) {
        if (String(s.brief_id) === id) db.remove(SOURCES, String(s.id));
      }
      for (const s of srcIn) {
        db.insert(SOURCES, {
          id: uuid(),
          brief_id: id,
          ordinal: Number(s.ordinal ?? 0),
          title: String(s.title ?? ""),
          url: String(s.url ?? ""),
          domain: domainOf(String(s.url ?? "")),
          snippet: String(s.snippet ?? ""),
          // Photographed source (on-device JPEG data-URL) — ALWAYS a string, never undefined, so the
          // web build never reads `undefined` off a source. "" for an ordinary web source.
          photo_ref: String(s.photo_ref ?? ""),
        });
      }
      db.update(BRIEFS, id, {
        status: "done",
        summary: String(i.summary ?? ""),
        body_json: String(i.body_json ?? "{}"),
        source_count: srcIn.length,
        elapsed_ms: Number(i.elapsed_ms ?? 0),
        updated_at: nowIso(),
      });
      return db.get(BRIEFS, id);
    },
    // Mark a run as failed (kept in history, retryable).
    fail_brief: ({ id }) => {
      db.update(BRIEFS, String(id), { status: "failed", updated_at: nowIso() });
      return null;
    },

    // ── reads ──
    list_briefs: ({ limit }) => {
      const cap = Math.min(Math.max(typeof limit === "number" ? limit : 100, 1), 500);
      return newestFirst(db.all(BRIEFS)).slice(0, cap).map(toMeta);
    },
    get_brief: ({ id }) => db.get(BRIEFS, String(id)),
    sources_for_brief: ({ id }) =>
      db
        .all(SOURCES)
        .filter((s) => String(s.brief_id) === String(id))
        .sort((a, b) => Number(a.ordinal ?? 0) - Number(b.ordinal ?? 0))
        .map((s) => ({
          ordinal: Number(s.ordinal ?? 0),
          title: String(s.title ?? ""),
          url: String(s.url ?? ""),
          domain: String(s.domain ?? ""),
          snippet: String(s.snippet ?? ""),
          photo_ref: String(s.photo_ref ?? ""),
        })),
    brief_count: () => db.all(BRIEFS).filter((b) => String(b.status) === "done").length,

    // On-device history search (local, no brain call). Mirrors db.rs::search_history:
    // empty query → newest briefs; otherwise AND-of-tokens, then OR fallback.
    search_history: ({ query, limit }) => {
      const cap = Math.min(Math.max(typeof limit === "number" ? limit : 100, 1), 500);
      const tokens = ftsTokens(String(query ?? ""));
      const briefs = newestFirst(db.all(BRIEFS));
      if (tokens.length === 0) return briefs.slice(0, cap).map(toMeta);
      const haystack = (b: Row) =>
        `${String(b.question ?? "")}\n${String(b.summary ?? "")}\n${String(b.body_json ?? "")}`.toLowerCase();
      for (const mode of ["and", "or"] as const) {
        const hits = briefs.filter((b) => {
          const h = haystack(b);
          return mode === "and" ? tokens.every((t) => h.includes(t)) : tokens.some((t) => h.includes(t));
        });
        if (hits.length > 0) return hits.slice(0, cap).map(toMeta);
      }
      return [];
    },

    // ── deletes ──
    delete_brief: ({ id }) => {
      const bid = String(id);
      for (const s of db.all(SOURCES)) {
        if (String(s.brief_id) === bid) db.remove(SOURCES, String(s.id));
      }
      db.remove(BRIEFS, bid);
      return null;
    },
    // Wipe all on-device research (clear history / account delete).
    clear_history: () => {
      for (const s of db.all(SOURCES)) db.remove(SOURCES, String(s.id));
      for (const b of db.all(BRIEFS)) db.remove(BRIEFS, String(b.id));
      return null;
    },
    // ── Web search / page read ────────────────────────────────────────────────
    // WEB-SURFACE RETRIEVAL — the browser does it, from the customer's own IP.
    //
    // This used to throw "web search runs on your device". That was true of the MECHANISM (a page
    // cannot read an arbitrary site) but it left the web build with no retrieval at all, quietly
    // falling back to the vendor-native path — wired for ONE vendor — so a web customer on any
    // other key got an answer presented as sourced with nothing retrieved.
    //
    // Now: Tier 1 sources the browser may read directly (no middleman), then a keyless reader for
    // the open web. Same principle as the phone — the customer's own connection, never our server,
    // so hundreds of customers are hundreds of IPs and there is no shared limit to hit.
    // Detail + the live measurements: champion-lifecycle/WEB-SEARCH-FEATURE.md
    web_search: async ({ query, limit }) => {
      // The screenshot driver can still inject its own fetcher; it just isn't required any more.
      const hook = (globalThis as unknown as { __ORIORA_DRIVER_SEARCH__?: (q: string, n: number) => Promise<unknown> }).__ORIORA_DRIVER_SEARCH__;
      if (hook) return await hook(String(query ?? ""), typeof limit === "number" ? limit : 5);
      return await browserSearch(String(query ?? ""), typeof limit === "number" ? limit : 5);
    },
    web_fetch: async ({ url, maxChars }) => {
      const hook = (globalThis as unknown as { __ORIORA_DRIVER_FETCH__?: (u: string, n: number) => Promise<string> }).__ORIORA_DRIVER_FETCH__;
      if (hook) return await hook(String(url ?? ""), typeof maxChars === "number" ? maxChars : 6000);
      return await browserFetch(String(url ?? ""), typeof maxChars === "number" ? maxChars : 6000);
    },

  };
}
